/**
 * Encrypted keystore — xchain-keystore compatible format.
 *
 * Lifted from leodex-v2/src/features/wallet/keystore-creation.ts.
 *
 * **Key difference**: the original uses PBKDF2(password) to derive the AES key.
 * We instead use a **random 256-bit AES key** generated client-side, stored
 * in IndexedDB. No password ever required from the user.
 *
 * Format remains xchain-keystore-compatible (cipher: aes-128-ctr, MAC: blake2b-256)
 * so the encrypted blob can interop with ASGARDEX/Vultisig if needed — but
 * those tools can't decrypt our keystores because they expect a password-derived
 * key, not a stored random one. That's fine: this format is just a convenient
 * envelope; we're not chasing interop, we're chasing reuse of proven primitives.
 */

import { blake2b } from "@noble/hashes/blake2.js";
import { bytesToHex, hexToBytes, randomBytes, toBuffer } from "./bytes";

const KEYSTORE_CIPHER = "aes-128-ctr";
const KEYSTORE_META = "teleswap-keystore-v1";

export type KeystoreFile = {
  crypto: {
    cipher: string;
    ciphertext: string;
    cipherparams: { iv: string };
    /** "random" marks this as a stored-random-key keystore (no KDF). */
    kdf: "random";
    /** Empty since we don't derive — kept for shape parity. */
    kdfparams: Record<string, never>;
    mac: string;
  };
  id: string;
  version: number;
  meta: string;
};

const assertSubtle = (): SubtleCrypto => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error("Secure browser crypto is unavailable.");
  return subtle;
};

/** Generate a fresh 32-byte AES key for keystore encryption. */
export const generateEncryptionKey = (): Uint8Array => randomBytes(32);

const generateKeystoreId = (): string => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = randomBytes(16);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = bytesToHex(bytes);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Encrypt a mnemonic phrase with the provided 32-byte AES key.
 * Produces an xchain-keystore-shaped JSON envelope (cipher: aes-128-ctr,
 * MAC: blake2b-256 over MAC-key || ciphertext).
 *
 * The 32-byte key is split: first 16 bytes used for AES, second 16 bytes for the MAC.
 */
export const encryptPhrase = async (
  phrase: string,
  key: Uint8Array,
): Promise<KeystoreFile> => {
  if (key.length !== 32) throw new Error("Encryption key must be exactly 32 bytes.");
  const subtle = assertSubtle();
  const iv = randomBytes(16);

  const cryptoKey = await subtle.importKey(
    "raw",
    toBuffer(key.slice(0, 16)),
    { name: "AES-CTR" },
    false,
    ["encrypt"],
  );

  const ciphertext = new Uint8Array(
    await subtle.encrypt(
      { name: "AES-CTR", counter: toBuffer(iv), length: 128 },
      cryptoKey,
      toBuffer(new TextEncoder().encode(phrase)),
    ),
  );

  // MAC = blake2b-256(key[16..32] || ciphertext)
  const macInput = new Uint8Array(16 + ciphertext.length);
  macInput.set(key.slice(16, 32), 0);
  macInput.set(ciphertext, 16);
  const mac = blake2b(macInput, { dkLen: 32 });

  return {
    crypto: {
      cipher: KEYSTORE_CIPHER,
      ciphertext: bytesToHex(ciphertext),
      cipherparams: { iv: bytesToHex(iv) },
      kdf: "random",
      kdfparams: {},
      mac: bytesToHex(mac),
    },
    id: generateKeystoreId(),
    version: 1,
    meta: KEYSTORE_META,
  };
};

/** Decrypt a keystore with the provided 32-byte AES key. Throws on MAC mismatch. */
export const decryptPhrase = async (file: KeystoreFile, key: Uint8Array): Promise<string> => {
  if (key.length !== 32) throw new Error("Encryption key must be exactly 32 bytes.");
  if (file.crypto.cipher !== KEYSTORE_CIPHER) {
    throw new Error(`Unsupported cipher: ${file.crypto.cipher}`);
  }

  const ciphertext = hexToBytes(file.crypto.ciphertext);
  const iv = hexToBytes(file.crypto.cipherparams.iv);

  // Verify MAC first (constant-time-ish via length check + byte-by-byte compare)
  const macInput = new Uint8Array(16 + ciphertext.length);
  macInput.set(key.slice(16, 32), 0);
  macInput.set(ciphertext, 16);
  const expectedMac = blake2b(macInput, { dkLen: 32 });
  const providedMac = hexToBytes(file.crypto.mac);
  if (!constantTimeEqual(expectedMac, providedMac)) {
    throw new Error("Keystore MAC verification failed — wrong key or tampered blob.");
  }

  const subtle = assertSubtle();
  const cryptoKey = await subtle.importKey(
    "raw",
    toBuffer(key.slice(0, 16)),
    { name: "AES-CTR" },
    false,
    ["decrypt"],
  );

  const plaintext = await subtle.decrypt(
    { name: "AES-CTR", counter: toBuffer(iv), length: 128 },
    cryptoKey,
    toBuffer(ciphertext),
  );

  return new TextDecoder().decode(plaintext);
};

const constantTimeEqual = (a: Uint8Array, b: Uint8Array): boolean => {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
};
